<template>
    <div class="container-fluid mt-5">
        <div class="card" data-toggle="lists">
            <div class="card-header">
                <div class="row align-items-center">
                    <div class="col-auto">
                        <button class="btn btn-white" @click="print">
                            <i class="fe fe-printer mr-2"/>
                            {{ $t('Print') }}
                        </button>
                    </div>
                    <div class="col"></div>

                    <div class="col-auto">
                        <date-filter v-model="dates" :initial="'today'"></date-filter>
                    </div>
                </div>
                <!-- / .row -->
            </div>

            <div class="card-body" v-if="isDataLoading">
                <div class="row align-items-center active">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
            <div class="card-body" id="printContent">
                <h2 class="text-center" v-if="company.billing">"{{ company.billing.legalName }}"</h2>
                <div class="row">
                    <div class="col-4">
                        <div class="card">
                            <div class="card-body">
                                <div class="row align-items-center">
                                    <div class="col">
                                        <h6 class="card-title mb-2">{{ $t('Date of operations') }}</h6>
                                        <span class="h2 mb-0">{{ dates.end | moment('DD MMMM YYYY') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="card">
                            <div class="card-body">
                                <div class="row align-items-center">
                                    <div class="col">
                                        <h6 class="card-title mb-2">{{ $t('Start of the day') }}</h6>
                                        <span class="h2 mb-0 showOnPrint">
                                            <money :value="money.start"/>
                                        </span>
                                        <money-field v-model="money.start" class="hideOnPrint"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="card">
                            <div class="card-body">
                                <div class="row align-items-center">
                                    <div class="col">
                                        <h6 class="card-title mb-2">{{ $t('End of the day') }}</h6>

                                        <span class="h2 mb-0 showOnPrint">
                                            <money :value="money.end"/>
                                        </span>
                                        <money-field v-model="money.end" class="hideOnPrint"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <table class="table">
                    <thead>
                    <tr>
                        <th width="5%">#</th>
                        <th width="50%">{{ $t('Customer') }}</th>
                        <th>{{ $t('Cash') }}</th>
                        <th>{{ $t('Card') }}</th>
                    </tr>
                    </thead>
                    <tr v-for="(item, idx) in transactions" :key="idx">
                        <td>{{ idx + 1 }}</td>
                        <td>
                            <span v-if="item.customer">
                                {{ item.customer.first_name }}
                                {{ item.customer.last_name }}
                            </span>
                        </td>
                        <td>
                            <money :value="item.cash"/>
                        </td>
                        <td>
                            <money :value="item.card"/>
                        </td>
                    </tr>
                    <thead>
                    <th></th>
                    <th>{{ $t('Total') }}</th>
                    <th>
                        <money :value="totals.cash"/>
                    </th>
                    <th>
                        <money :value="totals.card"/>
                    </th>
                    </thead>
                </table>
                <div class="row">
                    <div class="col-4 offset-1">{{ $t('Responsible') }}:</div>
                    <div class="col-2"></div>
                    <div class="col-4 offset-1">{{ company.billing.lastName }} {{ company.billing.firstName }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import debounce from 'lodash/debounce';
import Money from '@/components/Money.vue';
import MoneyField from '@/components/MoneyField.vue';
import {printMixins} from '@/mixins/print.js'
import DateFilter from '@/components/DateFilter.vue';
import TeamFilter from "@/components/TeamFilter";
import Transactions from "../../People/Customer/Transactions";
import TransactionsTable from "@/components/TransactionsTable";
import {mapActions, mapGetters, mapState} from 'vuex';

export default {
    mixins: [printMixins],
    components: {
        TransactionsTable,
        Transactions,
        Money,
        MoneyField,
        DateFilter,
        TeamFilter
    },
    data() {
        return {
            dates: {},
            totals: {},
            money: {
                start: 0,
                end: 0,
            },
            params: {
                query: {
                    $limit: 1000,
                    $or: [{parentId: null}, {status: 'topup'}],
                    $sort: {
                        date: -1
                    }
                }
            },
        };
    },

    computed: {
        ...mapState('transactions', {isDataLoading: 'isFindPending'}),
        ...mapGetters('transactions', {findDataInStore: 'find'}),
        ...mapGetters('my-company', {
            getMyCompany: 'get'
        }),
        company() {
            return this.getMyCompany('mine');
        },


        transactions() {
            let transactions = this.findDataInStore(this.params).data;
            transactions = this.$groupBy(transactions, item => item.invoiceId);
            transactions = Object.values(transactions).map(items => {
                let empty = {
                    date: items[0].date,
                    customer: items[0].customer,
                    cash: 0,
                    card: 0,
                    total: 0
                };
                return items.reduce((total, transaction) => {
                    if (transaction.paymentMethod === 'cash') {
                        total.cash += transaction.paidTotal;
                        total.total += total.cash;
                    } else if (transaction.paymentMethod === 'card') {
                        total.card += transaction.paidTotal;
                        total.total += total.card;
                    }
                    return total
                }, empty)
            });
            let summary = transactions.reduce((summary, transaction) => {
                summary.cash += transaction.cash;
                summary.card += transaction.card;
                summary.total += transaction.total;
                return summary
            }, {cash: 0, card: 0, total: 0});
            // transactions.push(summary);
            this.totals = summary;
            return transactions;
        }
    },

    watch: {
        dates: debounce(function(dates) {
            this.fetchTransactions();
        }, 100),
        dateFilter: debounce(function(dates) {
            if (dates.start) {
                this.params.query.date = {
                    $gte: dates.start,
                    $lte: dates.end
                }
            } else {
                delete this.params.query.date;
            }
            this.fetchTransactions();
        }, 100),
    },
    mounted() {
    },
    methods: {
        ...mapActions('transactions', {findTransactions: 'find'}),

        fetchTransactions() {
            let params = {
                query: {
                    $limit: 10000,
                    $skip: (this.currentPage - 1) * this.perPage,
                    $sort: {
                        date: -1
                    }
                }
            };

            params.query.parentId = {$ne: null};
            params.query.paymentMethod = {$ne: null};
            params.query.cancelled = false;


            if (this.dates.start && this.dates.end) {
                params.query.date = {
                    $gte: this.dates.start,
                    $lte: this.dates.end
                }
            }
            this.params = params;
            this.findTransactions(this.params);
        },

    }
}
</script>

<style lang="scss">
.showOnPrint {
    display: none;
}

@media screen {
    #printContent {
        color: #000 !important;
        max-height: 85vh;
        overflow-y: auto;
        max-width: 100%;
    }
}

@media print {
    .card {
        border-color: #000 !important;
        border-width: 2px !important;
        color: #000;
    }
    thead tr {
        border-left: 0;
        border-top: 2px solid #000 !important;
        border-bottom: 2px solid #000 !important;
        color: #000;
    }
    thead th {
        border-top: 2px solid #000 !important;
        border-bottom: 2px solid #000 !important;
        background-color: #ccc !important;
        color: #000 !important;
    }
    td {
        border-top: 1px solid #000 !important;
    }
    .showOnPrint {
        display: block;
    }
    .hideOnPrint {
        display: none;
    }
}

</style>
